<template>
    <modal v-show="show" @close="handleClose" size="larger" :closeable="true" class="outlined">
        <template #body>
            <div class="relative" v-if="product">
                <div v-if="showFavouriteStar" class="absolute top-0 left-0 z-10">
                    <BetterTooltip placement="bottom">
                        <template #reference>
                            <span
                                v-if="favouritesLocked"
                                class="w-10 h-10 inline-flex items-center justify-center text-yellow-500"
                            >
                                <i class="fas fa-star text-lg" aria-hidden="true"></i>
                            </span>
                            <button
                                v-else
                                type="button"
                                class="favourite-star-btn w-10 h-10 inline-flex items-center justify-center rounded text-gray-400 hover:text-yellow-500 border-0 bg-transparent p-0"
                                :class="{ 'text-yellow-500': displayedFavourite }"
                                @click.prevent="toggleFavourite"
                            >
                                <i class="text-lg" :class="displayedFavourite ? 'fas fa-star' : 'far fa-star'" aria-hidden="true"></i>
                            </button>
                        </template>
                        <template #floating>
                            <div class="text-[11px]">
                                <template v-if="favouritesLocked">
                                    <div class="whitespace-nowrap">Favourite</div>
                                    <div>{{ removeFavouriteTooltipSuffix }}</div>
                                </template>
                                <template v-else-if="displayedFavourite">
                                    <div class="whitespace-nowrap">Remove from Favourites</div>
                                    <div>{{ removeFavouriteTooltipSuffix }}</div>
                                </template>
                                <template v-else><span class="whitespace-nowrap">Add to Favourites</span></template>
                            </div>
                        </template>
                    </BetterTooltip>
                </div>
                <div class="px-12">
                <div class="flex flex-wrap">
                    <div class="pr-6 w-full md:w-1/2">
                        <div class="featured text-center">
                            <img :src="'/storage/' + currentImage" class="image-large" />
                        </div>

                        <div class="gallery flex flex-wrap my-3" v-if="product.images">
                            <a href="#" @click.stop.prevent="handleSelectImage(product.image)">
                                <img :src="'/storage/' + product.image" class="image-thumbnail mr-1" />
                            </a>

                            <a v-for="image in product.images" :key="image.id" href="#" @click.stop.prevent="handleSelectImage(image.filename)">
                                <img :src="'/storage/' + image.filename" class="image-thumbnail mr-1" />
                            </a>
                        </div>
                    </div>

                    <div class="pr-6 w-full md:w-1/2">
                        <h3 v-text="product.title" class="mt-0 mb-6"></h3>

                        <p class="description mb-3" v-text="product.description"></p>

                        <div class="price mb-3">
                            ${{ displayPrice.toFixed(2) }} / {{ product.size }}
                        </div>

                        <p v-if="product.special_order" class="leading-tight !text-xs italic font-bold text-red mb-3">{{ specialOrderLeadLabel }}</p>

                        <add-to-cart
                            v-if="canPlaceOrders || isAdmin"
                            :product="product"
                            class="my-3 w-44"
                        />

                        <div class="mt-6" v-html="product.long_description"></div>
                    </div>
                </div>
                </div>
            </div>
        </template>

        <template #footer></template>
    </modal>
</template>

<script>
    import { computed } from 'vue';
    import { usePage } from '@inertiajs/inertia-vue3'
    import Modal from '@/Pages/Catalogue/Partials/Modal';
    import UseCustomerAccess from '@/Composables/UseCustomerAccess';
    import UseUserAuth from '@/Composables/UseUserAuth';
    import UseCart from '@/Composables/UseCart';
    import AddToCart from '@/Pages/Catalogue/Partials/AddToCart';
    import BetterTooltip from '@/Jetstream/BetterTooltip';
    import { shouldApplySplitCaseSurcharge as productShouldApplySplitCaseSurcharge } from '@/Composables/UseProductQuantity';
    import { specialOrderLeadTimeLabel } from '@/helper';

    export default {
        props: ['product', 'show'],

        setup (props) {
            const { isAdmin } = UseUserAuth(usePage().props.value.user);
            const { canPlaceOrders } = UseCustomerAccess();
            const { cart } = UseCart(usePage().props.value.user, usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0);

            const cartQuantity = computed(() => {
                if (!props.product) return 0;
                const item = cart.value.find(i => i.product && i.product.id === props.product.id);
                return item ? item.quantity : 0;
            });
            const displayPrice = computed(() => {
                if (!props.product) return 0;
                const q = cartQuantity.value;
                const activeCustomer = usePage().props.value.activeCustomer;
                if (productShouldApplySplitCaseSurcharge(q, activeCustomer, props.product) && props.product.split_case_price != null) return props.product.split_case_price;
                return props.product.price != null ? props.product.price : 0;
            });

            return {
                isAdmin,
                canPlaceOrders,
                cartQuantity,
                displayPrice,
            };
        },

        components: {
            Modal,
            AddToCart,
            BetterTooltip,
        },

        props: {
            product: { type: Object, default: null },
            show: { type: Boolean, default: false },
            favouriteProductIds: { type: Array, default: () => [] },
            favouriteDetails: { type: Object, default: () => ({}) },
            showFavouriteStar: { type: Boolean, default: false },
            customerFavouritesVisibleOnly: { type: Boolean, default: false },
        },

        data () {
            return {
                currentImage: null,
                favouriteToggling: false,
                optimisticFavourite: null,
            };
        },

        computed: {
            specialOrderLeadLabel () {
                return this.product ? specialOrderLeadTimeLabel(this.product.special_order_lead_hours) : '';
            },
            isFavourite () {
                return this.product && this.showFavouriteStar && this.favouriteProductIds.includes(this.product.id);
            },
            displayedFavourite () {
                return this.optimisticFavourite !== null ? this.optimisticFavourite : this.isFavourite;
            },
            favouritesLocked () {
                return this.customerFavouritesVisibleOnly && this.displayedFavourite;
            },
            removeFavouriteTooltipSuffix () {
                if (!this.product) return '';
                const detail = this.favouriteDetails && this.favouriteDetails[this.product.id];
                return !detail
                    ? 'Added by a previous order'
                    : (detail.created_by_user_name
                        ? `Added by ${detail.created_by_user_name} on ${this.formatFavouriteDate(detail.created_at)}`
                        : 'Added by a previous order');
            },
        },

        methods: {
            shouldApplySplitCaseSurcharge(quantity) {
                return productShouldApplySplitCaseSurcharge(quantity, this.$page.props.activeCustomer, this.product);
            },
            toggleFavourite () {
                if (!this.product || this.favouriteToggling || this.favouritesLocked) return;
                this.favouriteToggling = true;
                this.optimisticFavourite = !this.displayedFavourite;
                const isAdd = this.optimisticFavourite;
                const request = isAdd
                    ? axios.post(route('catalogue.favourites.store'), { product_id: this.product.id })
                    : axios.delete(route('catalogue.favourites.destroy', this.product.id));
                request
                    .then(({ data }) => {
                        if (data.success && data.favouriteProductIds) {
                            this.$emit('favourites-updated', {
                                favouriteProductIds: data.favouriteProductIds,
                                favouriteDetails: data.favouriteDetails || {},
                            });
                        }
                        this.optimisticFavourite = null;
                    })
                    .catch(() => {
                        this.optimisticFavourite = null;
                    })
                    .finally(() => {
                        this.favouriteToggling = false;
                    });
            },

            nl2br (str, is_xhtml) {
                if (typeof str === 'undefined' || str === null) {
                    return '';
                }
                var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
                return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
            },

            handleSelectImage (image) {
                this.currentImage = image;
            },

            handleClose () {
                this.$emit('close');
            },

            formatFavouriteDate (isoString) {
                if (!isoString) return '';
                const d = new Date(isoString);
                return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
            },
        },

        watch: {
            product () {
                this.currentImage = this.product.image;
            }
        },
    }
</script>

<style scoped>
    h3 {
        color: #000;
        font-size: 1.5rem;
        line-height: 1.2;
    }

    .unit {
        line-height: 1.2;
        color: #85C71E;
        font-weight: bold;
        font-size: 0.9rem;
    }

    .price {
        font-size: 1.375rem;
        color: #000;
    }

    @media (min-width: 1024px) {
        h3 {
            font-size: 2.125rem;
        }

        .price {
            font-size: 1.5rem;
        }
    }

    p {
        font-size: 16px;
    }

    .modal-container {
        padding: 30px;
    }

    .image-large {
        height: 180px;
        object-fit: cover;
        max-width: 100%;
    }

    @media (min-width: 1024px) {
        .image-large {
            height: auto;
            max-height: 360px;
        }
    }

    .image-thumbnail {
        width: 36px;
        height: 36px;
        object-fit: contain;
    }

    .favourite-star-btn:focus {
        outline: none;
        box-shadow: none;
    }

    .gallery {
        text-align: center;
    }

    @media (min-width: 768px) {
        .gallery {
            text-align: left;
        }
    }
</style>